import React,{createContext,useCallback,useContext,useMemo,useState} from 'react';
import Box from '@mui/material/Box';
import { parseTemplate } from '../../../parser';
import renderChildren from '../renderChildren';

const WizardContext=createContext<any>(null);
export const useWizard=()=>useContext(WizardContext);

const ruleValidators: Record<string, (value: any, arg?: string) => string | undefined> = {
 required: (value) => {
  if (value === undefined || value === null || value === '') return 'Required';
  if (Array.isArray(value) && value.length === 0) return 'Required';
  return undefined;
 },
 email: (value) => {
  if (!value) return undefined;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value)) ? undefined : 'Invalid email';
 },
 min: (value, arg) => {
  if (!value) return undefined;
  const min = Number(arg);
  return String(value).length >= min ? undefined : `Must be at least ${min} characters`;
 },
};

function validateByRules(values: any, rules: Record<string, string> = {}) {
 const errors: Record<string, string> = {};

 Object.entries(rules).forEach(([field, ruleText]) => {
  const parts = String(ruleText).split('|').filter(Boolean);

  for (const part of parts) {
   const [name, arg] = part.split(':');
   const message = ruleValidators[name]?.(values?.[field], arg);
   if (message) {
    errors[field] = message;
    break;
   }
  }
 });

 return errors;
}

function collectSteps(nodes: any[] = [], steps: any[] = []) {
 nodes.forEach((child) => {
  if (child?.type === 'wizard_step') {
   steps.push(child);
   return;
  }

  if (child?.children?.length) {
   collectSteps(child.children, steps);
  }
 });

 return steps;
}

function expandIncludes(nodes: any[] = [], context: any = {}): any[] {
 return nodes.flatMap((child) => {
  if (!child || typeof child !== 'object') return [child];

  if (child.type === 'include') {
   const template = context?.partials?.[child.props?.name];

   if (!template) {
    console.warn(`Partial not found: ${child.props?.name}`);
    return [];
   }

   return parseTemplate(template, context);
  }

  if (child.children?.length) {
   return [{
    ...child,
    children: expandIncludes(child.children, context),
   }];
  }

  return [child];
 });
}

function withActiveStep(nodes: any[] = [], activeStep: any, initialValues: any, validate: any): any[] {
 return nodes.map((child) => {
  if (!child || typeof child !== 'object') return child;

  if (child.type === 'wizard_step') {
   return child === activeStep ? child : null;
  }

  const props = { ...(child.props || {}) };

  if (child.type === 'form') {
   if (props.initialValues === undefined) props.initialValues = initialValues || {};
   props.validate = validate;
  }

  const children = child.children ? withActiveStep(child.children, activeStep, initialValues, validate) : child.children;

  return {
   ...child,
   props,
   children,
  };
 }).filter(Boolean);
}

function getStepTabs(steps: any[] = []) {
 return steps.map((item, index) => ({
  index,
  label: item?.props?.label || `Step ${index + 1}`,
  icon: item?.props?.icon,
  iconPosition: item?.props?.iconPosition,
  sx: item?.props?.tabSx,
  disabled: item?.props?.disabled,
 }));
}

export default function WizardNode({node,children,initialValues,initialStep=0,context}:any){
 const [step,setStep]=useState(initialStep);
 const [stepErrors,setStepErrors]=useState<Record<number, number>>({});
 const sourceChildren = useMemo(
  () => expandIncludes(node?.children, context),
  [node?.children, context]
 );
 const steps = useMemo(() => collectSteps(sourceChildren), [sourceChildren]);
 const total=steps.length;
 const activeStep = steps[Math.min(step, Math.max(total - 1, 0))];
 const activeValidation = activeStep?.props?.validation || {};
 const validate = useMemo(() => (values: any) => validateByRules(values, activeValidation), [activeValidation]);
 const tabs = useMemo(() => getStepTabs(steps), [steps]);
 const labels = useMemo(() => tabs.map((item) => item.label), [tabs]);
 const reportStepErrors = useCallback((stepIndex: number, errors: any = {}) => {
  const count = Object.keys(errors || {}).length;
  setStepErrors((current) => ({
   ...current,
   [stepIndex]: count,
  }));
 }, []);
 const renderedChildren: any = sourceChildren
  ? renderChildren(withActiveStep(sourceChildren, activeStep, initialValues, validate), context)
  : React.Children.toArray(children).filter(Boolean);
 const value=useMemo(()=>({
  step,
  total,
  tabs,
  labels,
  stepErrors,
  fields:Object.keys(activeValidation),
  reportStepErrors,
  next:()=>setStep((s:number)=>Math.min(s+1,total-1)),
  back:()=>setStep((s:number)=>Math.max(s-1,0)),
  goTo:setStep
 }),[step,total,tabs,labels,stepErrors,activeValidation,reportStepErrors]);
 return <WizardContext.Provider value={value}><Box>{renderedChildren}</Box></WizardContext.Provider>
}
